import { getProcessorsByUser, deleteAnnualEnvironment, postAnnualEnvironment, getAnnualEnvironment2, putAnnualEnvironment } from '@/api/dataentry-api-generated';
import DeleteDialog from '@/components/DeleteDialog.vue';
import SaveDialog from '@/components/SaveDialog.vue';
import { defineComponent, ref } from '@vue/runtime-core';
export default defineComponent({
    name: 'AnnualEnvironmentComponent',
    components: { DeleteDialog, SaveDialog },
    props: {
        propSelected: Array,
        propCreateNew: Boolean,
        propDuplicateRecord: Boolean,
        propId: String
    },
    data: function () {
        return {
            filter: '',
            effectiveDates: '',
            createNew: this.propCreateNew,
            duplicateRecord: this.propDuplicateRecord,
            id: this.propId,
            loading: false,
            processors: [],
            item: {},
            totalHSCWProcessedForYear: 0,
            processorOptions: [{
                    label: '',
                    value: ''
                }],
            processorDisplay: {
                label: '',
                value: ''
            },
            tablePagination: {
                sortBy: 'site',
                descending: true,
                rowsPerPage: 50
            },
            signedAPCOValue: ref(),
            dropdownOptions: ['Yes', 'No'],
            showConfirmation: false,
            percentageThreshold: 70,
            waterUsagePerHSCWProcessedBenchmark: 7.3,
            energyUsagePerHSCWProcessedBenchmark: 2897,
            wasteSentOffSitePerHSCWProcessedBenchmark: 21.5 // kg/t HSCW
        };
    },
    computed: {
        uuid() {
            return this.$route.params.uuid;
        },
        selectedItems() {
            return this.propSelected;
        },
        buttonLabel() {
            return 'SAVE';
        },
        years() {
            const currentYear = new Date().getFullYear();
            const currentDate = new Date();
            // Set the financial year start date to July 1st
            const financialYearStartDate = new Date(currentYear, 6, 1);
            // Generate values from the past 20 years until the current year
            let yearOptions = Array.from({ length: 21 }, (_, i) => `${currentYear - i} (Jul 1 ${(currentYear - i) - 1} - Jun 30 ${currentYear - i})`);
            // Add an option for the 2025 financial year if July 1st has already passed
            if (currentDate > financialYearStartDate) {
                yearOptions.unshift(`${currentYear + 1} (Jul 1 ${currentYear} - Jun 30 ${currentYear + 1})`);
            }
            return yearOptions;
        }
    },
    created() {
        this.getProcessors();
        if (!this.createNew) {
            this.getItem(this.uuid, true);
        }
        if (this.duplicateRecord) {
            if (this.id !== undefined) {
                this.getItem(this.id, false);
            }
        }
    },
    methods: {
        async getItem(uuid, isUpdate) {
            this.loading = true;
            try {
                const result = await getAnnualEnvironment2(uuid);
                this.item = result;
                if (isUpdate) {
                    this.item.yearEntry = result.yearEntry;
                    this.processorDisplay.label = result.processorLabel;
                    this.processorDisplay.value = result.processor;
                    this.signedAPCOValue = result.signedAPCO === null ? undefined : result.signedAPCO === true ? 'Yes' : 'No';
                }
                else {
                    this.item.yearEntry = new Date().getFullYear();
                    this.signedAPCOValue = result.signedAPCO === null ? undefined : result.signedAPCO === true ? 'Yes' : 'No';
                }
                this.loading = false;
            }
            catch (error) {
                const errMsg = error;
                if (errMsg.message.includes('ResourceNotFoundException')) {
                    this.closeDialog();
                    this.$log.addError(error);
                    this.loading = false;
                }
            }
        },
        saveItem() {
            let isTotalHSCWProcessedForYearValid = false;
            let isWaterUsageWithinBenchmarkRange = false;
            let isEnergyUsageWithinBenchmarkRange = false;
            let isWasteSentOffSiteWithinBenchmarkRange = false;
            let isWasteSentOffSiteTotalValid = false;
            // Validate that the total HSCW processed for the year is not zero
            if (this.totalHSCWProcessedForYear <= 0) {
                const msg = 'The total HSCW processed for the year must be greater than zero.';
                const errorMsg = {
                    type: 'warning',
                    textColor: 'black',
                    timeout: 5000,
                    position: 'top',
                    message: msg,
                    html: true
                };
                this.$log.addCustomMessage(errorMsg);
            }
            else {
                isTotalHSCWProcessedForYearValid = true;
            }
            // Validate that the water, energy, and waste totals are within the benchmark range
            isWaterUsageWithinBenchmarkRange = this.validateTotalWaterIntakeToSite();
            isEnergyUsageWithinBenchmarkRange = this.validateTotalEnergyUsage();
            isWasteSentOffSiteWithinBenchmarkRange = this.validateWasteSentOffSiteTotal();
            // Validate that the sum of the organic and non-organic waste sent off-site is equal to the total
            if ((this.item.wasteSentOffSiteOrganic !== undefined && this.item.wasteSentOffSiteOrganic !== null) &&
                (this.item.wasteSentOffSiteNonOrganic !== undefined && this.item.wasteSentOffSiteNonOrganic !== null)) {
                const wasteTotalCalculated = this.item.wasteSentOffSiteOrganic + this.item.wasteSentOffSiteNonOrganic;
                if (this.item.wasteSentOffSiteTotal !== wasteTotalCalculated) {
                    const msg = 'The sum of organic and non-organic waste sent off-site must be equal to the total.';
                    const errorMsg = {
                        type: 'warning',
                        textColor: 'black',
                        timeout: 5000,
                        position: 'top',
                        message: msg,
                        html: true
                    };
                    this.$log.addCustomMessage(errorMsg);
                }
                else {
                    isWasteSentOffSiteTotalValid = true;
                }
            }
            if (isTotalHSCWProcessedForYearValid &&
                isWasteSentOffSiteTotalValid) {
                // Save or update the item
                if (!this.createNew) {
                    this.updateItem();
                }
                else {
                    if (isWaterUsageWithinBenchmarkRange &&
                        isEnergyUsageWithinBenchmarkRange &&
                        isWasteSentOffSiteWithinBenchmarkRange) {
                        this.createItem();
                    }
                    else {
                        this.showConfirmation = true;
                    }
                }
            }
        },
        validateTotalWaterIntakeToSite() {
            let result = false;
            if (this.item.totalWaterIntakeToSite !== undefined && this.item.totalWaterIntakeToSite !== null) {
                // Convert water intake from GL to KL
                const waterIntakeKL = this.item.totalWaterIntakeToSite * 1000000;
                // Convert HSCW processed from KG to tonnes
                const hscwTonnes = this.totalHSCWProcessedForYear / 1000;
                // Calculate actual water usage with new measurement unit and threshold bounds
                const actualWaterUsage = waterIntakeKL / hscwTonnes;
                const thresholdFactor = this.percentageThreshold / 100;
                const lowerBound = this.waterUsagePerHSCWProcessedBenchmark * (1 - thresholdFactor);
                const upperBound = this.waterUsagePerHSCWProcessedBenchmark * (1 + thresholdFactor);
                if (actualWaterUsage < lowerBound || actualWaterUsage > upperBound) {
                    // If the result is outside the threshold set, then display a warning
                    const msg = `The water intake to site total falls outside of the ${this.percentageThreshold}% threshold. Please re-check your entry and edit it if applicable.`;
                    const errorMsg = {
                        type: 'warning',
                        textColor: 'black',
                        timeout: 5000,
                        position: 'top',
                        message: msg,
                        html: true
                    };
                    this.$log.addCustomMessage(errorMsg);
                }
                else {
                    result = true;
                }
            }
            return result;
        },
        validateTotalEnergyUsage() {
            let result = false;
            if (this.item.totalEnergyUse !== undefined && this.item.totalEnergyUse !== null) {
                // Convert HSCW processed from KG to tonnes
                const hscwTonnes = this.totalHSCWProcessedForYear / 1000;
                // Calculate actual energy usage with new measurement unit and threshold bounds
                const actualEnergyUsage = this.item.totalEnergyUse / hscwTonnes;
                const thresholdFactor = this.percentageThreshold / 100;
                const lowerBound = this.energyUsagePerHSCWProcessedBenchmark * (1 - thresholdFactor);
                const upperBound = this.energyUsagePerHSCWProcessedBenchmark * (1 + thresholdFactor);
                if (actualEnergyUsage < lowerBound || actualEnergyUsage > upperBound) {
                    // If the result is outside the threshold set, then display a warning
                    const msg = `The energy usage total falls outside of the ${this.percentageThreshold}% threshold. Please re-check your entry and edit it if applicable.`;
                    const errorMsg = {
                        type: 'warning',
                        textColor: 'black',
                        timeout: 5000,
                        position: 'top',
                        message: msg,
                        html: true
                    };
                    this.$log.addCustomMessage(errorMsg);
                }
                else {
                    result = true;
                }
            }
            return result;
        },
        validateWasteSentOffSiteTotal() {
            let result = false;
            if (this.item.wasteSentOffSiteTotal !== undefined && this.item.wasteSentOffSiteTotal !== null) {
                // Convert waste total from tonnes to KG
                const wasteTotalKG = this.item.wasteSentOffSiteTotal * 1000;
                // Convert HSCW processed from KG to tonnes
                const hscwTonnes = this.totalHSCWProcessedForYear / 1000;
                // Calculate actual waste total with new measurement unit and threshold bounds
                const actualWasteTotal = wasteTotalKG / hscwTonnes;
                const thresholdFactor = this.percentageThreshold / 100;
                const lowerBound = this.wasteSentOffSitePerHSCWProcessedBenchmark * (1 - thresholdFactor);
                const upperBound = this.wasteSentOffSitePerHSCWProcessedBenchmark * (1 + thresholdFactor);
                if (actualWasteTotal < lowerBound || actualWasteTotal > upperBound) {
                    // If the result is outside the threshold set, then display a warning
                    const msg = `The waste sent off-site total falls outside of the ${this.percentageThreshold}% threshold. Please re-check your entry and edit it if applicable.`;
                    const errorMsg = {
                        type: 'warning',
                        textColor: 'black',
                        timeout: 5000,
                        position: 'top',
                        message: msg,
                        html: true
                    };
                    this.$log.addCustomMessage(errorMsg);
                }
                else {
                    result = true;
                }
            }
            return result;
        },
        confirmSave() {
            // Close the pop-up
            this.showConfirmation = false;
            // Save the record
            if (this.createNew) {
                this.createItem();
            }
        },
        async updateItem() {
            // Validate the form before proceeding
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.loading = true;
                const req = this.item;
                req.yearEntry = this.item.yearEntry;
                req.signedAPCO = this.signedAPCOValue === 'Yes' ? true : this.signedAPCOValue === 'No' ? false : null;
                try {
                    const req = this.item;
                    req.processor = this.processorDisplay.value;
                    await putAnnualEnvironment(this.uuid, this.item);
                    this.$log.addMessage('Successfully Updated Annual Environment');
                    this.$router.push({ name: 'Annual Environments' });
                }
                catch (error) {
                    this.$log.addError(error);
                    this.loading = false;
                }
            }
        },
        async createItem() {
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.loading = true;
                const req = this.item;
                req.yearEntry = this.item.yearEntry;
                req.processor = this.processorDisplay.value;
                req.signedAPCO = this.signedAPCOValue === 'Yes' ? true : this.signedAPCOValue === 'No' ? false : null;
                try {
                    await postAnnualEnvironment(req);
                    this.$log.addMessage('Successfully created new Annual Environment');
                    this.$emit('refresh');
                    this.closeDialog();
                }
                catch (error) {
                    const errorObj = error;
                    if (errorObj.message.includes('ResourceAlreadyExistsException')) {
                        const uuid = errorObj.message.substring(31, errorObj.message.length);
                        const msg = `A record with the same Annual Environment already exists: <a href="#/AnnualEnvironment/${uuid}">VIEW</a>`;
                        const errorMsg = {
                            type: 'warning',
                            textColor: 'black',
                            timeout: 5000,
                            position: 'top',
                            message: msg,
                            html: true
                        };
                        this.$log.addCustomMessage(errorMsg);
                    }
                    else {
                        this.$log.addError(error);
                    }
                    this.loading = false;
                }
            }
        },
        async deleteItem() {
            try {
                const result = await deleteAnnualEnvironment(this.uuid);
                this.item = result;
                this.$log.addMessage('Successfully deleted Annual Environment');
                this.closeDialog();
            }
            catch (error) {
                this.$log.addError(error);
            }
        },
        closeDialog() {
            if (this.createNew) {
                this.$emit('show-hide-dialog');
            }
            else {
                this.goBack();
            }
        },
        goBack() {
            this.$router.push({ name: 'Annual Environments' });
        },
        async getProcessors() {
            this.loading = true;
            try {
                const result = await getProcessorsByUser({ pageSize: 1000000, deleted: false });
                this.processors = result.items;
                this.processorOptions = this.getProcessorOptions();
                this.loading = false;
            }
            catch (error) {
                this.$log.addError(error);
                this.loading = false;
            }
        },
        getProcessorOptions() {
            // Display and sort the options in alphabetical order
            return this.processors.map(i => {
                return {
                    label: i.processorLabel,
                    value: i.uuid
                };
            }).sort((a, b) => a.label.localeCompare(b.label));
        },
        filterOptions(val, update) {
            if (val === '') {
                update(() => {
                    // No filter - display default list
                    this.processorOptions = this.getProcessorOptions();
                });
                return;
            }
            update(() => {
                const needle = val.toLowerCase();
                // Sort the search results in alphabetical order
                this.processorOptions = this.getProcessorOptions()
                    .filter(v => v.label.toLowerCase().indexOf(needle) > -1)
                    .sort((a, b) => a.label.localeCompare(b.label));
            });
        }
    }
});
